// EditInhouseProject.jsx
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
  Popover,
  MenuItem,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import AppToast from "../../components/AppToast";

/* ---------- COMMON STYLES ---------- */
const fieldLabelSx = {
  fontSize: 13,
  fontWeight: 500,
  color: COLORS.PRIMARY,
  mb: 0.5,
};

const inputSx = {
  "& .MuiInputBase-root": {
    height: 44,
    borderRadius: 2,
    "& fieldset": {
      borderColor: COLORS.PRIMARY,
    },
    "&:hover fieldset": {
      borderColor: COLORS.PRIMARY,
    },
    "&.Mui-focused fieldset": {
      borderColor: COLORS.PRIMARY,
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: COLORS.LIGHT_SECONDARY,
    opacity: 1,
  },
};

const grid3 = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr", md: "repeat(3, 1fr)" },
  gap: 2,
};

/* ---------- STATUS OPTIONS ---------- */
const statusOptions = [
  { value: "ACTIVE", label: "Active" },
  { value: "COMPLETED", label: "Completed" },
  { value: "ON_HOLD", label: "On Hold" },
  { value: "CANCELLED", label: "Cancelled" },
];

/* ---------- COMPONENT ---------- */
const EditInhouseProject = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth?.() || {};
  
  const [formData, setFormData] = useState({
    projectName: "",
    siteAddress: "",
    developerName: "",
    status: "ACTIVE",
    projectStartDate: null,
    projectEndDate: null,
  });
  
  const [stakeholders, setStakeholders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  
  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  // Refs for textarea height calculation
  const textareaRefs = useRef([]);

  // Date picker state
  const [startDatePickerAnchor, setStartDatePickerAnchor] = useState(null);
  const [endDatePickerAnchor, setEndDatePickerAnchor] = useState(null);

  // Track textarea heights
  const [textareaHeights, setTextareaHeights] = useState({});

  // Update textarea height based on content
  const updateTextareaHeight = (index) => {
    const textarea = textareaRefs.current[index];
    if (!textarea) return;

    textarea.style.height = "44px";
    const newHeight = Math.min(textarea.scrollHeight, 200);

    textarea.style.height = `${newHeight}px`;

    setTextareaHeights((prev) => ({
      ...prev,
      [index]: newHeight,
    }));
  };

  // Function to update all textarea heights after data is loaded
  const updateAllTextareaHeights = () => {
    setTimeout(() => {
      stakeholders.forEach((_, index) => {
        updateTextareaHeight(index);
      });
    }, 100);
  };

  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setFetching(true);
        
        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };
        
        const response = await axios.get(
          `${url}/api/inhouse-projects/fetch-by/${projectId}`,
          config
        );
        
        if (response.data.project) {
          const project = response.data.project;
          
          // Format dates for date picker
          const formatDateForPicker = (dateString) => {
            if (!dateString) return null;
            try {
              return new Date(dateString);
            } catch (error) {
              return null;
            }
          };
          
          // Set form data
          setFormData({
            projectName: project.projectName || "",
            siteAddress: project.siteAddress || "",
            developerName: project.developerName || "",
            status: project.status || "ACTIVE",
            projectStartDate: formatDateForPicker(project.projectStartDate),
            projectEndDate: formatDateForPicker(project.projectEndDate),
          });
          
          // Set stakeholders
          if (project.stakeholders && Array.isArray(project.stakeholders)) {
            const formattedStakeholders = project.stakeholders.map(stakeholder => ({
              role: stakeholder.role || "",
              name: stakeholder.name || "",
              details: stakeholder.details || "",
              isPrefilled: false,
            }));
            setStakeholders(formattedStakeholders);
            
            // Update textarea heights after stakeholders are set
            setTimeout(() => {
              formattedStakeholders.forEach((_, index) => {
                updateTextareaHeight(index);
              });
            }, 200);
          } else {
            // Default stakeholders if none exist
            const defaultStakeholders = [
              { role: "Client", name: "", details: "", isPrefilled: false },
              { role: "PMC", name: "Arconest Consultants", details: "", isPrefilled: true },
              { role: "Contractor", name: "", details: "", isPrefilled: false },
            ];
            setStakeholders(defaultStakeholders);
          }
        } else {
          showToast("Project not found", "error");
          navigate("/progress/inhouse-projects");
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        showToast("Failed to load project data. Please try again.", "error");
        navigate("/progress/inhouse-projects");
      } finally {
        setFetching(false);
      }
    };
    
    if (projectId && token) {
      fetchProject();
    }
  }, [projectId, token, navigate]);

  // Update textarea heights when stakeholders change
  useEffect(() => {
    if (stakeholders.length > 0 && !fetching) {
      updateAllTextareaHeights();
    }
  }, [stakeholders, fetching]);

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleStatusChange = (event) => {
    setFormData({
      ...formData,
      status: event.target.value,
    });
  };

  const handleStartDateChange = (date) => {
    setFormData({
      ...formData,
      projectStartDate: date,
    });
    setStartDatePickerAnchor(null);
  };

  const handleEndDateChange = (date) => {
    setFormData({
      ...formData,
      projectEndDate: date,
    });
    setEndDatePickerAnchor(null);
  };

  // Date picker click handler
  const handleCalendarClick = (event, type) => {
    if (type === 'start') {
      setStartDatePickerAnchor(event.currentTarget);
    } else {
      setEndDatePickerAnchor(event.currentTarget);
    }
  };

  const updateStakeholder = (index, field, value) => {
    const updated = [...stakeholders];
    updated[index][field] = value;
    
    // If it's a prefilled box and user edits, mark it as no longer prefilled
    if (field !== 'details' && updated[index].isPrefilled) {
      updated[index].isPrefilled = false;
    }
    
    setStakeholders(updated);

    // Update textarea height for details field
    if (field === 'details') {
      setTimeout(() => updateTextareaHeight(index), 0);
    }
  };

  const removeStakeholder = (index) => {
    // Allow removal of ALL rows - including prefilled ones
    if (stakeholders.length <= 1) {
      showToast("At least 1 stakeholder is required", "error");
      return;
    }
    
    const updated = stakeholders.filter((_, i) => i !== index);
    setStakeholders(updated);
    
    // Remove ref and height entry
    textareaRefs.current = textareaRefs.current.filter((_, i) => i !== index);
    setTextareaHeights(prev => {
      const newHeights = { ...prev };
      delete newHeights[index];
      // Shift remaining heights down
      Object.keys(newHeights).forEach(key => {
        if (key > index) {
          newHeights[key - 1] = newHeights[key];
          delete newHeights[key];
        }
      });
      return newHeights;
    });
  };

  const handleAddStakeholder = () => {
    const newStakeholder = { role: "", name: "", details: "", isPrefilled: false };
    setStakeholders([...stakeholders, newStakeholder]);
  };

  // Validate form
  const validateForm = () => {
    if (!formData.projectName.trim()) {
      return "Project name is required";
    }
    if (!formData.siteAddress.trim()) {
      return "Site address is required";
    }
    if (!formData.developerName.trim()) {
      return "Developer name is required";
    }
    if (!formData.projectStartDate) {
      return "Project start date is required";
    }
    if (!formData.projectEndDate) {
      return "Project end date is required";
    }
    if (new Date(formData.projectEndDate) <= new Date(formData.projectStartDate)) {
      return "End date must be after start date";
    }
    if (stakeholders.length === 0) {
      return "At least one stakeholder is required";
    }
    
    // Validate each stakeholder
    for (const stakeholder of stakeholders) {
      if (!stakeholder.role.trim()) {
        return "Stakeholder role is required";
      }
      if (!stakeholder.name.trim()) {
        return "Stakeholder name is required";
      }
      if (!stakeholder.details.trim()) {
        return "Stakeholder details are required";
      }
    }
    
    return null;
  };

  // Submit form
  const handleSubmit = async () => {
    const validationError = validateForm();
    if (validationError) {
      showToast(validationError, "error");
      return;
    }

    setLoading(true);

    try {
      const formattedStakeholders = stakeholders.map(stakeholder => ({
        role: stakeholder.role.toLowerCase(),
        name: stakeholder.name.trim(),
        details: stakeholder.details.trim(),
      }));

      const requestData = {
        projectName: formData.projectName.trim(),
        siteAddress: formData.siteAddress.trim(),
        developerName: formData.developerName.trim(),
        status: formData.status,
        stakeholders: formattedStakeholders,
        projectStartDate: formData.projectStartDate.toISOString(),
        projectEndDate: formData.projectEndDate.toISOString(),
      };

      const response = await axios.put(
        `${url}/api/inhouse-projects/update/${projectId}`,
        requestData,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        showToast("Project updated successfully!", "success");
        
        // Navigate back to project overview after delay
        setTimeout(() => {
          navigate(`/progress/edit-report/${projectId}`);
        }, 1500);
      }
    } catch (error) {
      console.error("Error updating project:", error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error || 
                          "Failed to update project. Please try again.";
      showToast(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  // Toast functions
  const showToast = (message, type = "success") => {
    const title = type === "success" ? "Success" : 
                  type === "error" ? "Error" : 
                  type === "warning" ? "Warning" : "Info";
    
    setToast({
      open: true,
      type,
      title,
      message,
    });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleCloseTab = () => {
    navigate(`/progress/edit-report/${projectId}`);
  };

  const formatDateForDisplay = (date) => {
    if (!date) return "";
    return format(date, "yyyy-MM-dd");
  };

  const startDatePickerOpen = Boolean(startDatePickerAnchor);
  const endDatePickerOpen = Boolean(endDatePickerAnchor);

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "ACTIVE":
        return "#8CD47E";
      case "COMPLETED":
        return "#4CAF50";
      case "ON_HOLD":
        return "#FF9800";
      case "CANCELLED":
        return "#F44336";
      default:
        return COLORS.PRIMARY;
    }
  };

  if (fetching) {
    return (
      <Box sx={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "100vh" 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        {/* ================= HEADER WITH TITLE AND CLOSE ICON ================= */}
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center", 
          mb: 3 
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H1, color: COLORS.PRIMARY }}>
              Edit Project
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseTab}
            disabled={loading}
            sx={{ 
              color: COLORS.PRIMARY,
              "&:hover": { backgroundColor: COLORS.LIGHT_SECONDARY + "20" }
            }}
          >
            <Icon name="close" color={COLORS.ERROR}/>
          </IconButton>
        </Box>

        {/* ================= PROJECT DETAILS ================= */}
        <Box sx={{ ...grid3, mb: 4 }}>
          <Box>
            <Typography sx={fieldLabelSx}>Project Details</Typography>
            <TextField
              fullWidth
              placeholder="Name of Project"
              sx={inputSx}
              value={formData.projectName}
              onChange={handleInputChange("projectName")}
              disabled={loading}
            />
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>Site Address</Typography>
            <TextField
              fullWidth
              placeholder="Detailed Address"
              sx={inputSx}
              value={formData.siteAddress}
              onChange={handleInputChange("siteAddress")}
              disabled={loading}
            />
          </Box>

          <Box>
            <Typography sx={fieldLabelSx}>Developer Name</Typography>
            <TextField
              fullWidth
              placeholder="Developer Name"
              sx={inputSx}
              value={formData.developerName}
              onChange={handleInputChange("developerName")}
              disabled={loading}
            />
          </Box>
        </Box>

        {/* ================= STAKEHOLDER SECTION ================= */}
        <Box sx={{ mb: 4 }}>
          {/* STAKEHOLDER HEADER */}
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.PRIMARY }}>
              Stakeholder Details
            </Typography>
          </Box>

          {/* STAKEHOLDER FORM */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 2 }}>
            {stakeholders.map((s, index) => (
              <Box
                key={index}
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr", md: "repeat(3, 1fr)" },
                  gap: 2,
                  alignItems: "flex-start",
                  position: "relative",
                }}
              >
                {/* Role */}
                <Box>
                  <Typography sx={fieldLabelSx}>Role</Typography>
                  <TextField
                    fullWidth
                    value={s.role}
                    placeholder="Role"
                    sx={inputSx}
                    onChange={(e) => updateStakeholder(index, "role", e.target.value)}
                    disabled={loading}
                  />
                </Box>

                {/* Name */}
                <Box>
                  <Typography sx={fieldLabelSx}>Name</Typography>
                  <TextField
                    fullWidth
                    value={s.name}
                    placeholder="Name"
                    sx={inputSx}
                    onChange={(e) => updateStakeholder(index, "name", e.target.value)}
                    disabled={loading}
                  />
                </Box>

                {/* Details with Close Icon */}
                <Box sx={{ position: "relative", pr: 5 }}>
                  <Typography sx={fieldLabelSx}>Details</Typography>

                  <textarea
                    ref={(el) => (textareaRefs.current[index] = el)}
                    value={s.details}
                    placeholder="Details"
                    disabled={loading}
                    onChange={(e) => {
                      updateStakeholder(index, "details", e.target.value);
                      updateTextareaHeight(index);
                    }}
                    style={{
                      width: "100%",
                      minHeight: "44px",
                      height: textareaHeights[index]
                        ? `${textareaHeights[index]}px`
                        : "44px",
                      boxSizing: "border-box",
                      padding: "10px 14px",
                      lineHeight: "20px",
                      borderRadius: "8px",
                      border: `1px solid ${COLORS.PRIMARY}`,
                      fontFamily: "inherit",
                      fontSize: "inherit",
                      color: COLORS.TEXT,
                      resize: "none",
                      outline: "none",
                      overflow: "hidden",
                      backgroundColor: loading ? "#f5f5f5" : "#fff",
                    }}
                  />
                  <style>
                    {`
                      textarea::placeholder {
                        color: ${COLORS.LIGHT_SECONDARY};
                        line-height: 20px;
                      }
                    `}
                  </style>
                  {/* Close Icon – OUTSIDE textarea */}
                  <IconButton
                    size="small"
                    onClick={() => removeStakeholder(index)}
                    disabled={loading || stakeholders.length <= 1}
                    sx={{
                      position: "absolute",
                      right: "8px",
                      top: "22px",
                      zIndex: 1,
                    }}
                  >
                    <Icon name="close2" color={COLORS.ERROR}/>
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>

          {/* ADD STAKEHOLDER BUTTON */}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              startIcon={<Icon name="plus2" />}
              onClick={handleAddStakeholder}
              sx={{
                height: 44,
                px: 3,
                bgcolor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: 2,
                "&:hover": { bgcolor: COLORS.SECONDARY },
              }}
              disabled={loading}
            >
              Add Stakeholder
            </Button>
          </Box>
        </Box>

        {/* ================= PROJECT DURATION ================= */}
        <Box sx={{ ...grid3, mb: 4 }}>
          {/* Start Date */}
          <Box>
            <Typography sx={fieldLabelSx}>Project Start Date</Typography>
            <TextField
              fullWidth
              placeholder="YYYY-MM-DD"
              value={formData.projectStartDate ? formatDateForDisplay(formData.projectStartDate) : ""}
              sx={inputSx}
              onClick={(e) => handleCalendarClick(e, 'start')}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={(e) => handleCalendarClick(e, 'start')}
                      sx={{ color: COLORS.PRIMARY }}
                      disabled={loading}
                    >
                      <Icon name="calendar2" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* End Date */}
          <Box>
            <Typography sx={fieldLabelSx}>Expected End Date</Typography>
            <TextField
              fullWidth
              placeholder="YYYY-MM-DD"
              value={formData.projectEndDate ? formatDateForDisplay(formData.projectEndDate) : ""}
              sx={inputSx}
              onClick={(e) => handleCalendarClick(e, 'end')}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={(e) => handleCalendarClick(e, 'end')}
                      sx={{ color: COLORS.PRIMARY }}
                      disabled={loading}
                    >
                      <Icon name="calendar2" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box />
        </Box>

        {/* ================= DATE PICKERS ================= */}
        {/* Popovers for Date Pickers */}
        <Popover
          open={startDatePickerOpen}
          anchorEl={startDatePickerAnchor}
          onClose={() => setStartDatePickerAnchor(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <StaticDatePicker
            value={formData.projectStartDate}
            onChange={handleStartDateChange}
            displayStaticWrapperAs="desktop"
          />
        </Popover>

        <Popover
          open={endDatePickerOpen}
          anchorEl={endDatePickerAnchor}
          onClose={() => setEndDatePickerAnchor(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <StaticDatePicker
            value={formData.projectEndDate}
            minDate={formData.projectStartDate || undefined}
            onChange={handleEndDateChange}
            displayStaticWrapperAs="desktop"
          />
        </Popover>

        {/* ================= SUBMIT BUTTON ================= */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCloseTab}
            disabled={loading}
            sx={{
              height: 44,
              px: 4,
              borderColor: COLORS.LIGHT_SECONDARY,
              color: COLORS.TEXT,
              textTransform: "none",
              borderRadius: 2,
              "&:hover": {
                borderColor: COLORS.PRIMARY,
                color: COLORS.PRIMARY,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name="save" />}
            onClick={handleSubmit}
            disabled={loading}
            sx={{
              height: 44,
              px: 4,
              bgcolor: COLORS.PRIMARY,
              textTransform: "none",
              borderRadius: 2,
              "&:hover": { bgcolor: COLORS.PRIMARY },
              "&:disabled": { bgcolor: COLORS.LIGHT_SECONDARY },
            }}
          >
            {loading ? "Updating Project..." : "Update Project"}
          </Button>
        </Box>

        {/* Toast Component */}
        <AppToast
          open={toast.open}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={handleCloseToast}
          autoHideDuration={5000}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default EditInhouseProject;